import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, Download, ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Global image lightbox with optional carousel.
 *
 * Backwards compatible: pass `src` + `downloadUrl` + `alt` for a single image.
 * For multiple images in one answer, pass `items=[{src, downloadUrl, alt}]`
 * and `index` to control which one is visible. Arrow keys and the on-screen
 * chevrons navigate; `onIndexChange` reports moves to the parent.
 */
export default function ImageLightbox({
  src,
  downloadUrl,
  alt = "",
  items,
  index = 0,
  onIndexChange,
  onClose,
}) {
  const list = Array.isArray(items) && items.length > 0
    ? items
    : src
      ? [{ src, downloadUrl, alt }]
      : [];
  const open = list.length > 0;

  // Lightbox owns the index when the parent doesn't track it.
  const [internalIdx, setInternalIdx] = useState(index);
  useEffect(() => { setInternalIdx(index); }, [index, items]);
  const idx = onIndexChange ? index : internalIdx;
  const setIdx = (next) => {
    if (onIndexChange) onIndexChange(next);
    else setInternalIdx(next);
  };

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      else if (list.length > 1 && e.key === "ArrowRight") setIdx((idx + 1) % list.length);
      else if (list.length > 1 && e.key === "ArrowLeft") setIdx((idx - 1 + list.length) % list.length);
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, idx, list.length]);

  if (!open) return null;
  const current = list[Math.max(0, Math.min(idx, list.length - 1))];
  const hasMany = list.length > 1;

  return createPortal(
    <div
      data-testid="image-lightbox-modal"
      className="fixed inset-0 z-[200] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <button
        onClick={(e) => { e.stopPropagation(); onClose?.(); }}
        className="absolute top-5 right-5 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
        data-testid="lightbox-close"
        aria-label="Close"
      >
        <X className="w-5 h-5" />
      </button>

      {hasMany && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); setIdx((idx - 1 + list.length) % list.length); }}
            className="absolute left-5 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
            data-testid="lightbox-prev"
            aria-label="Previous image"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); setIdx((idx + 1) % list.length); }}
            className="absolute right-5 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
            data-testid="lightbox-next"
            aria-label="Next image"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </>
      )}

      <div className="relative max-w-6xl max-h-[90vh] flex flex-col items-center gap-4" onClick={(e) => e.stopPropagation()}>
        <img
          src={current.src}
          alt={current.alt || ""}
          className="max-w-full max-h-[78vh] object-contain rounded-lg shadow-2xl"
        />
        <div className="flex items-center gap-3">
          {hasMany && (
            <span className="text-xs font-medium text-white/70 tabular-nums" data-testid="lightbox-counter">
              {idx + 1} / {list.length}
            </span>
          )}
          {current.downloadUrl && (
            <a
              href={current.downloadUrl}
              data-testid="lightbox-download"
              className="inline-flex items-center gap-2 h-11 px-5 rounded-md bg-white text-slate-900 hover:bg-slate-100 text-sm font-medium shadow"
            >
              <Download className="w-4 h-4" /> Download
            </a>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
